import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api';
import { ArrowLeft, Plus, Edit, Trash2, Mail, Shield, User, Users, Eye, EyeOff, FileSignature } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const UserManagement = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [roles, setRoles] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  
  // Obtener usuario actual para verificar permisos - verificar ambos storages
  const userDataStr = localStorage.getItem('user') || sessionStorage.getItem('user')
  const userData = JSON.parse(userDataStr || '{}')
  const isAdmin = userData.role === 'administrador' || userData.is_admin
  const isSupervisor = userData.role === 'supervisor'

  // Formulario de creación/edición
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    apellido: '',
    role: 'usuario'
  })

  // Formulario de invitación a firmar
  const [inviteFormData, setInviteFormData] = useState({
    documento_nombre: '',
    documento_url: '',
    coordenadas_x: '',
    coordenadas_y: '',
    pagina: 1,
    tipo_firma: 'texto'
  })

  useEffect(() => {
    loadUsers()
    loadRoles()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Error al cargar usuarios')
          navigate('/dashboard')
          return
        }
        throw new Error('Error al cargar usuarios')
      }
      
      const data = await response.json()
      setUsers(data)
    } catch (error) {
      toast.error('Error al cargar usuarios: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const loadRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/roles/available`)
      if (response.ok) {
        const data = await response.json()
        // Filtrar roles según el usuario actual - verificar ambos storages
        const userDataStr = localStorage.getItem('user') || sessionStorage.getItem('user')
        const userData = JSON.parse(userDataStr || '{}')
        const currentUserRole = userData.role || 'usuario'
        
        // Si es supervisor, no puede cambiar a administrador
        if (currentUserRole === 'supervisor') {
          setRoles(data.roles.filter(role => role.value !== 'administrador'))
        } else {
          // Administrador puede cambiar a todos los roles
          setRoles(data.roles)
        }
      }
    } catch (error) {
      console.error('Error al cargar roles:', error)
    }
  }

  const filteredUsers = users.filter(user =>
    user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreateUser = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Usuario creado exitosamente. Se ha enviado un correo con las credenciales.')
        setShowCreateModal(false)
        setFormData({ email: '', nombre: '', apellido: '', role: 'usuario' })
        loadUsers()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al crear usuario')
      }
    } catch (error) {
      toast.error('Error al crear usuario: ' + error.message)
    }
  }

  const handleEditUser = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Usuario actualizado exitosamente')
        setShowEditModal(false)
        setSelectedUser(null)
        setFormData({ email: '', nombre: '', apellido: '', role: 'usuario' })
        loadUsers()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al actualizar usuario')
      }
    } catch (error) {
      toast.error('Error al actualizar usuario: ' + error.message)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        toast.success('Usuario eliminado exitosamente')
        loadUsers()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al eliminar usuario')
      }
    } catch (error) {
      toast.error('Error al eliminar usuario: ' + error.message)
    }
  }

  const handleResetPassword = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres resetear la contraseña de este usuario?')) return

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok && data.success === true && data.email_sent === true) {
        toast.success(
          data.message ||
            'Se envió un enlace de restablecimiento. La contraseña aún no ha cambiado.',
        )
        loadUsers()
      } else {
        toast.error(
          data.detail ||
            data.message ||
            'No se pudo enviar el correo. La contraseña no fue modificada.',
        )
      }
    } catch (error) {
      toast.error('Error al resetear contraseña: ' + error.message)
    }
  }

  const handleToggleStatus = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/toggle-status`, {
        method: 'PUT'
      })

      if (response.ok) {
        const data = await response.json()
        toast.success(data.message)
        loadUsers() // Recargar lista para mostrar cambios
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al cambiar estado del usuario')
      }
    } catch (error) {
      toast.error('Error al cambiar estado del usuario: ' + error.message)
    }
  }

  const openEditModal = (user) => {
    setSelectedUser(user)
    setFormData({
      email: user.email,
      nombre: user.nombre,
      apellido: user.apellido,
      role: user.role
    })
    setShowEditModal(true)
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'administrador':
        return <Shield className="h-4 w-4 text-red-500" />
      case 'supervisor':
        return <User className="h-4 w-4 text-blue-500" />
      default:
        return <Users className="h-4 w-4 text-gray-500" />
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'administrador':
        return 'bg-red-100 text-red-800'
      case 'supervisor':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca'
    
    try {
      // La fecha viene en formato ISO desde el backend
      // Si no tiene 'Z' al final, asumir que es UTC y agregarlo
      let isoString = dateString
      if (!isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-', 10)) {
        isoString = isoString + 'Z'
      }
      
      const date = new Date(isoString)
      
      // Convertir a zona horaria de Colombia (America/Bogota, UTC-5)
      const options = {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true // Formato 12 horas con AM/PM
      }
      
      return date.toLocaleString('es-CO', options)
    } catch (error) {
      console.error('Error al formatear fecha:', error)
      return dateString
    }
  }

  const handleInviteToSign = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/invitar-firmante`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: selectedUser.email,
          nombre: selectedUser.nombre,
          apellido: selectedUser.apellido,
          documento_nombre: inviteFormData.documento_nombre,
          documento_url: inviteFormData.documento_url,
          coordenadas_x: inviteFormData.coordenadas_x ? parseInt(inviteFormData.coordenadas_x) : null,
          coordenadas_y: inviteFormData.coordenadas_y ? parseInt(inviteFormData.coordenadas_y) : null,
          pagina: inviteFormData.pagina || 1,
          tipo_firma: inviteFormData.tipo_firma || 'texto'
        })
      })

      if (response.ok) {
        const data = await response.json()
        toast.success(data.message || 'Invitación enviada exitosamente')
        setShowInviteModal(false)
        setSelectedUser(null)
        setInviteFormData({
          documento_nombre: '',
          documento_url: '',
          coordenadas_x: '',
          coordenadas_y: '',
          pagina: 1,
          tipo_firma: 'texto'
        })
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al enviar invitación')
      }
    } catch (error) {
      toast.error('Error al enviar invitación: ' + error.message)
    }
  }

  const openInviteModal = (user) => {
    setSelectedUser(user)
    setInviteFormData({
      documento_nombre: '',
      documento_url: '',
      coordenadas_x: '',
      coordenadas_y: '',
      pagina: 1,
      tipo_firma: 'texto'
    })
    setShowInviteModal(true)
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-title">
              <h1>Gestión de Usuarios</h1>
              <p>Administra usuarios del sistema y sus permisos</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div></div>
            {/* Administrador y supervisor pueden crear usuarios */}
            {(isAdmin || isSupervisor) && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Crear Usuario</span>
              </button>
            )}
          </div>
        </div>

        {/* Lista de Usuarios */}
        <div className="card">
        {/* Barra de búsqueda */}
        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <input
            type="text"
            placeholder="Buscar usuarios por nombre, email o rol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field w-full md:w-1/3"
          />
          {searchTerm && (
            <span className="text-sm text-gray-500 ml-2 flex items-center gap-2">
              {filteredUsers.length} resultado{filteredUsers.length !== 1 ? 's' : ''}
              <button 
                onClick={() => setSearchTerm('')} 
                className="text-gray-400 hover:text-gray-600"
                title="Limpiar búsqueda"
              >
                ✕
              </button>
            </span>
          )}
        </div>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando usuarios...</p>
          </div>
        ) : (searchTerm && filteredUsers.length === 0) ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No se encontraron usuarios
            </h3>
            <p className="text-gray-600">
              No hay usuarios que coincidan con "{searchTerm}"
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-primary-600 hover:text-primary-800"
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay usuarios registrados
            </h3>
            <p className="text-gray-600">
              Crea el primer usuario del sistema
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Acceso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.nombre} {user.apellido}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(user.role)}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                        {user.must_change_password && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Cambio de contraseña requerido
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.last_login ? formatDate(user.last_login) : 'Nunca'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar usuario"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className={user.is_active ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"}
                          title={user.is_active ? "Desactivar usuario" : "Activar usuario"}
                        >
                          {user.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Resetear contraseña"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openInviteModal(user)}
                          className="text-green-600 hover:text-green-900"
                          title="Invitar a firmar documento"
                        >
                          <FileSignature className="h-4 w-4" />
                        </button>
                        {/* Administrador y supervisor pueden eliminar usuarios */}
                        {(isAdmin || isSupervisor) && user.email !== "admin@induma.com.co" && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar usuario"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>

      </div>

      {/* Modal de Crear Usuario */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Crear Nuevo Usuario</h3>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellido
                    </label>
                    <input
                      type="text"
                      value={formData.apellido}
                      onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="input-field"
                    required
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Crear Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Editar Usuario */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Editar Usuario</h3>
            <form onSubmit={handleEditUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellido
                    </label>
                    <input
                      type="text"
                      value={formData.apellido}
                      onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="input-field"
                    required
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Actualizar Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Invitar a Firmar */}
      {showInviteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Invitar a {selectedUser.nombre} {selectedUser.apellido} a Firmar
            </h3>
            <form onSubmit={handleInviteToSign}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Documento
                  </label>
                  <input
                    type="text"
                    value={inviteFormData.documento_nombre}
                    onChange={(e) => setInviteFormData({ ...inviteFormData, documento_nombre: e.target.value })}
                    className="input-field"
                    placeholder="Ej: Contrato de Servicios"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL del Documento
                  </label>
                  <input
                    type="text"
                    value={inviteFormData.documento_url}
                    onChange={(e) => setInviteFormData({ ...inviteFormData, documento_url: e.target.value })}
                    className="input-field"
                    placeholder={`${import.meta.env.VITE_API_URL}/uploads/documento.pdf`}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL completa del documento PDF que se firmará
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coordenada X (opcional)
                    </label>
                    <input
                      type="number"
                      value={inviteFormData.coordenadas_x}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, coordenadas_x: e.target.value })}
                      className="input-field"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coordenada Y (opcional)
                    </label>
                    <input
                      type="number"
                      value={inviteFormData.coordenadas_y}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, coordenadas_y: e.target.value })}
                      className="input-field"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Página
                    </label>
                    <input
                      type="number"
                      value={inviteFormData.pagina}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, pagina: parseInt(e.target.value) || 1 })}
                      className="input-field"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Firma
                    </label>
                    <select
                      value={inviteFormData.tipo_firma}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, tipo_firma: e.target.value })}
                      className="input-field"
                    >
                      <option value="texto">Texto</option>
                      <option value="imagen">Imagen</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false)
                    setSelectedUser(null)
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Enviar Invitación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
